import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadUsersToStore } from 'store/actions/user';
import { useUserService } from 'hooks/api/iam/user';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDataTable from 'app/shared/datatable/web/datatable';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/member/layouts/generic';
import { Board, BoardCard } from 'app/shared/board';
import { ViewToggle } from '../shared/view-toggle';
import { USER_COLUMNS } from './constants';
import { table_config } from './helper';

const segmentOf = (user) => {
  if (user.is_active === false) return 'inactive';
  if (user.account_type === 'agent') return 'agent';
  if (user.account_type === 'affiliate') return 'affiliate';
  return 'regular';
};

export const UsersBoard = () => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { fetchUsers, searchUsers } = useUserService();
  const users_in_store = useSelector((state) => state.users);
  const { is_mobile_view } = useSelector((state) => state.metadata);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active_view, setActiveView] = useState('board');
  const [is_search_mode, setSearchMode] = useState(false);
  const [user_count, setUserCount] = useState(0);

  useEffect(() => {
    if (active_view === 'board') handleDataRequest(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active_view]);

  useEffect(() => {
    setUsers(Object.values(users_in_store).map((user) => ({ ...user, segment: segmentOf(user) })));
  }, [users_in_store]);

  const handleDataRequest = async (page = 0, population = 200) => {
    try {
      setLoading(true);
      const { users } = await fetchUsers({
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });
      dispatch(loadUsersToStore(users));
    } catch {
      dispatch(loadUsersToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleListDataRequest = async (
    page,
    population = 10,
    options = { sort_query: '', reset: false }
  ) => {
    try {
      setLoading(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { users, size } = await fetchUsers({
        query_string: `${sort_query}&page=${page}&population=${population}`
      });
      setUserCount(size);

      dispatch(loadUsersToStore(users));
    } catch {
      dispatch(loadUsersToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleListSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { users, size } = await searchUsers(keys, keyword, {
        query_string: `page=${page}&population=${population}`
      });
      setUserCount(size);

      dispatch(loadUsersToStore(users));
    } catch {
      dispatch(loadUsersToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleDatatableAction = ({ name, type, data }) => {
    if (type === 'single') navigateTo(`/users/${data.id}`);
  };

  const handleItemClick = (user) => navigateTo(`/users/${user.id}`);

  const list_items = [...users].sort(
    (a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0)
  );

  const renderCard = (user) => {
    const name = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email || '—';

    return (
      <BoardCard
        key={user.id}
        title={name}
        subtitle={user.email}
        meta={user.account_type || 'regular'}
        onClick={() => navigateTo(`/users/${user.id}`)}
      />
    );
  };

  return (
    <>
      <GridRow num_of_columns={12}>
        <GridColumn span={3}>
          <ViewToggle active_view={active_view} onChange={setActiveView} />
        </GridColumn>
        <GridColumn span={9} />
      </GridRow>

      <Spacer multiple={2} />

      {active_view === 'board' && (
        <Board
          columns={USER_COLUMNS}
          items={users}
          group_by="segment"
          loading={loading}
          empty_text="No users"
          renderCard={renderCard}
        />
      )}

      {active_view === 'list' &&
        (is_mobile_view ? (
          <MobileDatatable
            config={{ ...table_config, is_search_mode, items: list_items, total_count: user_count }}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ) : (
          <WebDataTable
            config={{ ...table_config, is_search_mode, items: list_items, total_count: user_count }}
            action={handleDatatableAction}
            onClick={handleItemClick}
            loading_data={loading}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ))}
    </>
  );
};
